import { useTranslation } from 'react-i18next';
import { ContentLibraryContentDto } from '@asset-metrix/portal-gateway-client-js';
import { Checkbox, LoadingIndicator } from '@asset-metrix/portal-ui';

import { contentTitle } from './groupContentsForDisplay';

import styles from './addComponentToDashboardModal.module.scss';

export type SelectComponentsStepProps = {
  charts: ContentLibraryContentDto[];
  displayContents: ContentLibraryContentDto[];
  isError: boolean;
  isPending: boolean;
  isSuccess: boolean;
  onToggleIndex: (index: number, checked: boolean) => void;
  selectedContentIndexes: Set<number>;
  tables: ContentLibraryContentDto[];
  untitledLabel: string;
};

export function SelectComponentsStep({
  charts,
  displayContents,
  isError,
  isPending,
  isSuccess,
  onToggleIndex,
  selectedContentIndexes,
  tables,
  untitledLabel,
}: SelectComponentsStepProps) {
  const { t } = useTranslation();

  if (isPending) {
    return (
      <LoadingIndicator
        className={styles['loading']}
        data-testid="add-components-contents-loading"
        size="2em"
      />
    );
  }

  if (isError) {
    return (
      <p className={styles['message']}>
        {t('app.page.editAction.addComponentModal.searchError')}
      </p>
    );
  }

  if (!isSuccess) {
    return null;
  }

  if (displayContents.length === 0) {
    return (
      <p className={styles['message']}>
        {t('app.page.editAction.addComponentModal.noResults')}
      </p>
    );
  }

  const renderContentList = (
    sectionContents: ContentLibraryContentDto[],
    startIndex: number
  ) =>
    sectionContents.map((item, offset) => {
      const index = startIndex + offset;
      return (
        <Checkbox
          key={`${item.content.id}-${index}`}
          checked={selectedContentIndexes.has(index)}
          label={contentTitle(item, untitledLabel)}
          onChange={(checked) => onToggleIndex(index, checked)}
        />
      );
    });

  return (
    <>
      <p className={styles['instruction']}>
        {t('app.page.editAction.addComponentModal.selectComponentsInstruction')}
      </p>
      <div className={styles['columns']}>
        {tables.length > 0 && (
          <div className={styles['list']}>
            <p className={styles['section-title']}>
              {t('app.page.editAction.addComponentModal.tables')}
            </p>
            {renderContentList(tables, 0)}
          </div>
        )}
        {charts.length > 0 && (
          <div className={styles['list']}>
            <p className={styles['section-title']}>
              {t('app.page.editAction.addComponentModal.charts')}
            </p>
            {renderContentList(charts, tables.length)}
          </div>
        )}
      </div>
    </>
  );
}
